// frontend/src/components/dashboard/pos/dialogs/ChangeDialog.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  Button,
  Grid,
  Divider,
  useTheme,
  useMediaQuery,
  Slide
} from '@mui/material';
import { 
  MonetizationOn, 
  CheckCircle,
  LocalAtm
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { gradients } from '../styles/posStyles';

// Create a transition component for the dialog
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ChangeDialog = ({
  open,
  onClose,
  totalAmount,
  amountReceived,
  changeAmount
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const MotionDiv = motion.div;
  
  // Responsive breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Handle dialog close with proper cleanup - prevent backdrop click
  const handleClose = (event, reason) => {
    // Prevent closing on backdrop click
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
    
    // Clear any focused elements
    if (document.activeElement && document.activeElement.blur) {
      document.activeElement.blur();
    }
    onClose(event, reason);
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Transition}
      disableRestoreFocus={true}
      disableAutoFocus={false}
      disableEnforceFocus={false}
      keepMounted={false}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 2 : 4,
          background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 50%, #a7f3d0 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(16, 185, 129, 0.2)',
          boxShadow: '0 25px 50px -12px rgba(16, 185, 129, 0.25)',
          maxWidth: isMobile ? '90vw' : '480px',
          width: '100%',
          overflow: 'hidden',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 30% 20%, rgba(16, 185, 129, 0.1) 0%, transparent 50%)',
            pointerEvents: 'none'
          }
        }
      }}
      aria-labelledby="change-dialog-title"
      aria-describedby="change-dialog-description"
    >
      <DialogContent sx={{ 
        textAlign: 'center', 
        p: isMobile ? 3 : 4,
        position: 'relative',
        zIndex: 1
      }}>
        {/* Success Icon with Animation */}
        <MotionDiv
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ 
            type: "spring",
            stiffness: 200,
            damping: 15,
            delay: 0.2
          }}
        >
          <Box sx={{ 
            position: 'relative',
            display: 'inline-block',
            mb: isMobile ? 2 : 3
          }}>
            <CheckCircle sx={{ 
              fontSize: isMobile ? 80 : 100, 
              color: '#10b981',
              filter: 'drop-shadow(0 4px 8px rgba(16, 185, 129, 0.3))'
            }} />
            <MonetizationOn sx={{ 
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              fontSize: isMobile ? 32 : 40,
              color: 'white'
            }} />
          </Box>
        </MotionDiv>

        {/* Title */}
        <MotionDiv
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <Typography 
            id="change-dialog-title"
            variant={isMobile ? "h5" : "h4"} 
            gutterBottom 
            sx={{ 
              color: '#059669', 
              fontWeight: 700,
              textShadow: '0 2px 4px rgba(5, 150, 105, 0.1)',
              mb: isMobile ? 1 : 2
            }}
          >
            {t('pos.dialog.paymentComplete')}
          </Typography>
        </MotionDiv>

        {/* Amount Details */}
        <MotionDiv
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <Box sx={{ 
            my: isMobile ? 2 : 3, 
            p: isMobile ? 2 : 3, 
            background: 'rgba(16, 185, 129, 0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            border: '1px solid rgba(16, 185, 129, 0.2)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '2px',
              background: 'linear-gradient(90deg, #10b981, #059669, #10b981)',
            }
          }}>
            <Grid container spacing={isMobile ? 1 : 2}>
              <Grid item xs={6} sx={{ textAlign: 'left' }}>
                <Typography 
                  variant={isMobile ? "body2" : "body1"}
                  sx={{ 
                    color: '#047857',
                    fontWeight: 500,
                    fontSize: isMobile ? '0.85rem' : '1rem'
                  }}
                >
                  {t('pos.total')}:
                </Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: 'right' }}>
                <Typography 
                  variant={isMobile ? "body2" : "body1"}
                  sx={{ 
                    fontWeight: 600, 
                    color: '#059669',
                    fontSize: isMobile ? '0.85rem' : '1rem'
                  }}
                >
                  ${totalAmount.toFixed(2)}
                </Typography>
              </Grid>
              
              <Grid item xs={6} sx={{ textAlign: 'left' }}>
                <Typography 
                  variant={isMobile ? "body2" : "body1"}
                  sx={{ 
                    color: '#047857',
                    fontWeight: 500,
                    fontSize: isMobile ? '0.85rem' : '1rem'
                  }}
                >
                  {t('pos.dialog.cashReceived')}:
                </Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: 'right' }}>
                <Typography 
                  variant={isMobile ? "body2" : "body1"}
                  sx={{ 
                    fontWeight: 600, 
                    color: '#059669',
                    fontSize: isMobile ? '0.85rem' : '1rem'
                  }}
                >
                  ${parseFloat(amountReceived).toFixed(2)}
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                <Divider sx={{ 
                  my: isMobile ? 1 : 1.5, 
                  borderColor: 'rgba(16, 185, 129, 0.3)',
                  borderWidth: 1
                }} />
              </Grid>
              
              <Grid item xs={6} sx={{ textAlign: 'left' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LocalAtm sx={{ 
                    mr: 0.5, 
                    color: '#047857',
                    fontSize: isMobile ? 16 : 20
                  }} />
                  <Typography 
                    variant={isMobile ? "h6" : "h5"} 
                    sx={{ 
                      color: '#047857', 
                      fontWeight: 700,
                      fontSize: isMobile ? '1.1rem' : '1.25rem'
                    }}
                  >
                    {t('pos.dialog.changeDue')}:
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: 'right' }}>
                <Typography 
                  variant={isMobile ? "h6" : "h5"} 
                  sx={{ 
                    fontWeight: 800, 
                    color: '#10b981',
                    fontSize: isMobile ? '1.3rem' : '1.5rem',
                    textShadow: '0 2px 4px rgba(16, 185, 129, 0.2)'
                  }}
                >
                  ${changeAmount.toFixed(2)}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </MotionDiv>

        {/* Action Button */}
        <MotionDiv
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.8 }}
        >
          <Button
            fullWidth
            variant="contained"
            onClick={() => handleClose(null, 'buttonClick')}
            size={isMobile ? "medium" : "large"}
            sx={{
              mt: isMobile ? 2 : 3,
              background: gradients.primary,
              color: 'white',
              borderRadius: 2,
              py: isMobile ? 1.5 : 2,
              fontSize: isMobile ? '1rem' : '1.1rem',
              fontWeight: 600,
              boxShadow: '0 10px 20px rgba(102, 126, 234, 0.3)',
              '&:hover': {
                background: gradients.primaryHover,
                boxShadow: '0 15px 30px rgba(102, 126, 234, 0.4)',
                transform: 'translateY(-2px)'
              },
              '&:active': {
                transform: 'translateY(0px)'
              },
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            {t('common.done')}
          </Button>
        </MotionDiv>

        {/* Floating Success Message */}
        <MotionDiv
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 1 }}
        >
          <Typography 
            id="change-dialog-description"
            variant="body2" 
            sx={{ 
              color: '#047857',
              mt: isMobile ? 1 : 2,
              fontSize: isMobile ? '0.8rem' : '0.875rem',
              fontStyle: 'italic'
            }}
          >
            {t('pos.dialog.thankYou') || 'Thank you for your business!'}
          </Typography>
        </MotionDiv>
      </DialogContent>
    </Dialog>
  );
};

export default ChangeDialog;